import { invoke } from "@tauri-apps/api/core";
import { emit, listen } from "@tauri-apps/api/event";

import {
  beginTextAttentionSession,
  endTextAttentionSession,
  finishTextAttentionHold,
  ingestVoiceScrollDuringText,
  ingestTextAttentionGesture,
  ingestTextAttentionViewChange,
  setTextAttentionComment,
  useTextAttentionStore,
} from "./text-attention-store";
import { useVoiceAttentionStore } from "./voice-attention-store";
import { VOICE_ATTENTION_START_EVENT } from "./use-voice-attention";

const SESSION_EVENT = "text-attention-session";
const CLEAR_EVENT = "text-attention-clear";
const COMMENT_EVENT = "text-attention-comment";

type Gesture = {
  recordingId: string;
  clipId?: string | null;
  strokeId: string;
  phase: "start" | "move" | "end";
  tMs: number;
  displayId: number | null;
  x: number;
  y: number;
};

let listening = false;

export function useTextAttentionLifecycle(): void {
  if (listening) return;
  listening = true;
  void listen<{ recordingId: string }>(SESSION_EVENT, (event) => {
    beginTextAttentionSession(event.payload.recordingId);
  });
  void listen<{ recordingId: string }>(CLEAR_EVENT, (event) => {
    endTextAttentionSession(event.payload.recordingId);
  });
  void listen<{ id: string; comment: string }>(COMMENT_EVENT, (event) => {
    setTextAttentionComment(event.payload.id, event.payload.comment);
  });
  void listen<Gesture>("voice-attention-gesture", (event) => {
    if (event.payload.clipId) return;
    if (
      event.payload.phase === "start" &&
      useTextAttentionStore.getState().recordingId === event.payload.recordingId
    ) {
      window.dispatchEvent(new Event(VOICE_ATTENTION_START_EVENT));
    }
    ingestTextAttentionGesture(event.payload);
  });
  void listen<Parameters<typeof ingestTextAttentionViewChange>[0]>(
    "voice-attention-view-change",
    (event) => {
      if (
        event.payload.recordingId ===
        useTextAttentionStore.getState().recordingId
      ) {
        ingestTextAttentionViewChange(event.payload);
      } else if (
        event.payload.recordingId ===
        useVoiceAttentionStore.getState().recordingId
      ) {
        ingestVoiceScrollDuringText(event.payload);
      }
    },
  );
  void listen<{ recordingId: string }>("text-attention-shift-up", (event) => {
    finishTextAttentionHold(event.payload.recordingId);
  });
}

export async function startTextAttentionSession(): Promise<string> {
  const recordingId = crypto.randomUUID();
  beginTextAttentionSession(recordingId);
  try {
    await invoke("set_text_attention_active", { active: true, recordingId });
    await emit(SESSION_EVENT, { recordingId });
    return recordingId;
  } catch (error) {
    endTextAttentionSession(recordingId);
    void emit(CLEAR_EVENT, { recordingId }).catch(() => {});
    throw error;
  }
}

export async function stopTextAttentionSession(
  recordingId: string,
): Promise<void> {
  endTextAttentionSession(recordingId);
  try {
    await invoke("set_text_attention_active", { active: false, recordingId });
  } finally {
    await emit(CLEAR_EVENT, { recordingId });
  }
}

export function updateTextAttentionComment(id: string, comment: string): void {
  setTextAttentionComment(id, comment);
  void emit(COMMENT_EVENT, { id, comment }).catch(() => {});
}
